import React, { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import {
    Form,
    FormFields,
    FormActions,
    FormDropdown,
    mapToDropdownOption,
    ValidationResult,
    FormValidationSummary,
    useNotifications,
    FormCheckbox,
    FormTextField,
    FormNumberTextField,
    mapEmptyStringToUndefined,
    mapNullableToUndefined
} from "sprint-common-web";

import { useAspectRatios, AspectRatio } from "../../../../../../common/data/lookup-data-providers/useAspectRatios";
import { useAspectTypes, AspectType } from "../../../../../../common/data/lookup-data-providers/useAspectTypes";
import { useStandards } from "../../../../../../common/data/lookup-data-providers/useStandards";
import { Standard } from "../../../../../../common/types/Standard";
import { validateDuration } from "../../../../../../content-ingestion/file-records/common/utils/validateDuration";
import { validateResolution } from "../../../../../../content-ingestion/file-records/common/utils/validateResolution";
import { useOnPremAssetDetails } from "../../../common/hooks/useOnPremAssetDetails";
import { ensureOnPremVideoDetails } from "../../../OnPremAssetDetails";
import { useEditOnPremVideoAssetTechnical } from "./useEditOnPremVideoAssetTechnical";

export interface VideoTechnicalFormProps {
    onDismiss(): void;
    onSubmit(newAssetId: string): void;
    assetId: string;
}

interface VideoTechnicalFormData {
    aspectRatioId: string;
    aspectTypeId: string;
    duration: string;
    encoding: boolean;
    framerate: string;
    resolution: string;
    standardId: string;
    bitrate: number;
}

export const OnPremVideoAssetTechnicalForm = (props: VideoTechnicalFormProps): JSX.Element => {
    const [isLoading, setIsLoading] = useState<boolean>(false);
    const [originalAspectRatio, setOriginalAspectRatio] = useState<AspectRatio | undefined>(undefined);
    const [validationResult, setValidationResult] = useState<ValidationResult>();
    const { success, error } = useNotifications();
    const { data: assetDetails } = useOnPremAssetDetails();
    const { validateOnPremVideoAssetTechnical, updateOnPremVideoAssetTechnical } = useEditOnPremVideoAssetTechnical({
        invalidationKey: assetDetails?.assetId
    });

    const { data: aspectRatios } = useAspectRatios();
    const { data: aspectTypes } = useAspectTypes();
    const { data: standards } = useStandards();

    if (assetDetails) {
        ensureOnPremVideoDetails(assetDetails);
    }

    const { handleSubmit, errors, control, formState, reset, getValues } = useForm<VideoTechnicalFormData>({
        defaultValues: {
            aspectRatioId: undefined,
            aspectTypeId: undefined,
            standardId: undefined,
            duration: assetDetails?.technical?.duration,
            encoding: assetDetails?.technical?.encoding,
            framerate: assetDetails?.technical?.framerate,
            resolution: assetDetails?.technical?.resolution,
            bitrate: assetDetails?.technical?.videoBitRate
        }
    });

    useEffect(() => {
        let defaultAspectRatio = undefined;
        let defaultAspectType = undefined;
        let defaultStandard = undefined;

        if (aspectRatios) {
            defaultAspectRatio = aspectRatios?.find(
                (aspectRatio: AspectRatio) => aspectRatio.id === assetDetails?.technical?.aspectRatio?.id
            );
            setOriginalAspectRatio(defaultAspectRatio);
        }

        if (aspectTypes) {
            defaultAspectType = aspectTypes?.find(
                (aspectType: AspectType) => aspectType.id === assetDetails?.technical?.aspectType?.id
            );
        }

        if (standards) {
            defaultStandard = standards?.find(
                (standard: Standard) => standard.id === assetDetails?.technical?.standard?.id
            );
        }

        reset({
            ...getValues(),
            aspectRatioId: defaultAspectRatio?.id,
            aspectTypeId: defaultAspectType?.id,
            standardId: defaultStandard?.id
        });
    }, [aspectRatios, aspectTypes, standards]);

    const hasAspectRatioChanged = (aspectRatioId: string): boolean => {
        return originalAspectRatio?.id !== aspectRatioId;
    };

    const handleUpdate = async ({
        aspectRatioId,
        aspectTypeId,
        standardId,
        bitrate,
        duration,
        encoding,
        framerate,
        resolution
    }: VideoTechnicalFormData) => {
        setIsLoading(true);

        try {
            if (hasAspectRatioChanged(aspectRatioId)) {
                const result = await validateAndSetResult(aspectRatioId);

                if (!result.isValid) {
                    setIsLoading(false);
                    return;
                }
            }

            const updateResponse = await updateOnPremVideoAssetTechnical.mutateAsync({
                assetId: props.assetId,
                aspectRatioId,
                aspectTypeId: mapEmptyStringToUndefined(aspectTypeId),
                bitrate: mapNullableToUndefined(bitrate),
                duration,
                encoding,
                framerate: mapEmptyStringToUndefined(framerate),
                resolution: mapEmptyStringToUndefined(resolution),
                standardId: mapEmptyStringToUndefined(standardId)
            });

            props.onSubmit(updateResponse.newAssetId);
            success("Video asset technical details updated successfully.");
        } catch (e) {
            error("Failed to update video asset technical details.");
        } finally {
            setIsLoading(false);
        }
    };

    const validateAndSetResult = async (aspectRatioId: string) => {
        const result = await validateOnPremVideoAssetTechnical.mutateAsync({
            assetId: props.assetId,
            aspectRatioId
        });
        setValidationResult(result);

        return result;
    };

    return (
        <Form onSubmit={handleSubmit(handleUpdate)}>
            <FormFields>
                <FormDropdown
                    options={aspectRatios?.map((ratio) => mapToDropdownOption(ratio)) ?? []}
                    control={control}
                    label="Aspect Ratio"
                    name="aspectRatioId"
                    disabled={!aspectRatios?.length}
                    errors={errors}
                    disableClear={true}
                    rules={{ required: "Aspect Ratio is required" }}
                />
                <FormDropdown
                    options={aspectTypes?.map((type) => mapToDropdownOption(type)) ?? []}
                    control={control}
                    label="Aspect Type"
                    name="aspectTypeId"
                    disabled={!aspectTypes?.length}
                    errors={errors}
                />
                <FormCheckbox label="Encoding (Proresolution)" name="encoding" control={control} />
                <FormTextField label="Framerate" name="framerate" control={control} errors={errors} />
                <FormNumberTextField label="Bitrate" name="bitrate" control={control} errors={errors} />{" "}
                <FormTextField
                    rules={{ validate: validateDuration, required: "Duration is required" }}
                    label="Duration"
                    name="duration"
                    control={control}
                    errors={errors}
                    mask="99:99:99"
                />
                <FormTextField
                    label="Resolution"
                    name="resolution"
                    control={control}
                    errors={errors}
                    rules={{ validate: validateResolution, required: "Resolution is required" }}
                />
                <FormDropdown
                    options={standards?.map((type) => mapToDropdownOption(type)) ?? []}
                    control={control}
                    label="Standard"
                    name="standardId"
                    errors={errors}
                    disabled={!standards?.length}
                />
                {validationResult && <FormValidationSummary validationResult={validationResult} />}
                <FormActions
                    onCancel={props.onDismiss}
                    isLoading={formState.isValidating || formState.isSubmitting || isLoading}
                    submitDisabled={!formState.isDirty}
                />
            </FormFields>
        </Form>
    );
};
